// © Thibault Sabathier, 2026. frontend/src/utils/messageNormalization.js
//
// Forme canonique d'un message reçu par socket.
//
// ── POURQUOI CETTE FONCTION EXISTE ────────────────────────────────────────
// C'est une LISTE BLANCHE : tout champ du payload serveur qui n'est pas
// recopié ici est perdu, sans erreur ni trace. `attachments` en était absent,
// et le symptôme était trompeur — le destinataire recevait un message vide en
// direct, puis voyait les fichiers apparaître au rechargement. La cause était
// donc côté client alors que tout désignait le backend.
//
// Ce comportement est une décision, pas un accident : on ne recopie que ce que
// le rendu consomme, pour ne pas laisser un payload serveur injecter des
// champs arbitraires dans l'état partagé. Mais il impose une règle :
//
//   ⚠️ TOUT NOUVEAU CHAMP DE MESSAGE doit être ajouté ICI, en plus du backend.
//
// Extraite du hook pour être testable : `useFloatingMessaging.js` importe
// `apiClient`, qui lit `window.location` au chargement, donc rien de ce
// fichier-là n'est atteignable par le runner du dépôt (environment: 'node',
// sans jsdom).

/**
 * @param {object} message   Payload brut du socket.
 * @param {object} senderInfo Expéditeur déjà résolu par l'appelant (il peut
 *   venir du payload, du cache des conversations, ou d'un objet temporaire).
 * @param {string} [nowIso]  Horodatage de repli, injectable pour les tests.
 * @returns {object|null} `null` si le payload est inexploitable.
 */
export const normalizeIncomingMessage = (message, senderInfo, nowIso) => {
  if (!message || !message.id || !message.conversation_id) return null

  // Un seul horodatage pour les deux champs. C'est la SEULE différence
  // assumée avec le code d'origine, qui appelait `new Date()` deux fois et
  // pouvait donc produire un `updated_at` postérieur d'une milliseconde à
  // `created_at` sur un message qui n'a jamais été modifié.
  const stamp = nowIso || new Date().toISOString()

  return {
    id: message.id,
    // `Number()` : le serveur peut rendre l'identifiant en chaîne selon le
    // chemin d'émission, et les comparaisons en aval sont strictes (`===`).
    conversation_id: Number(message.conversation_id),
    sender_id: message.sender_id,
    content: message.content,
    created_at: message.created_at || message.createdAt || stamp,
    updated_at: message.updated_at || message.updatedAt || stamp,
    sender: senderInfo,
    is_system_message: message.is_system_message || false,
    // `|| []` pour que le rendu n'ait jamais à distinguer « champ absent » de
    // « aucune pièce jointe ».
    attachments: message.attachments || [],
  }
}

export default normalizeIncomingMessage
